/**
 * Scheduled Reports (Month-End Reports) – internal API resource.
 * Uses improve-api /api/_internal/scheduled-reports/* via generated client (gen).
 * Types are imported from gen (Swagger/Orval); legacy names re-exported for compatibility.
 */
import {
	getImproveAPI,
	ScheduledReportsControllerGetOverviewParams,
	ScheduledReportsControllerGetTopPerformersParams,
	ScheduledReportsControllerGetUsersWithoutCompletionsParams,
	ScheduledReportsControllerGetDivisionsParams,
	ScheduledReportsControllerGetSupervisorsParams,
	ScheduledReportOverviewDto,
	ScheduledReportTopPerformerDto,
	ScheduledReportUserWithoutRowDto,
	ScheduledReportDivisionRowDto,
	ScheduledReportSupervisorRowDto,
	GetUsersWithoutCompletionsResponseDto,
	GetDivisionsResponseDto,
	GetSupervisorsResponseDto,
	SavedReportMetaDto,
	GetSavedReportByIdResponseDto,
	GetDistributionRecipientsResponseDto,
	AddDistributionRecipientsResponseDto,
	RemoveDistributionRecipientResponseDto,
	GenerateReportBody,
} from "../gen";
import { useImproveAxios } from "../../hooks/useImproveAxios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BASE = "/api/_internal/scheduled-reports";

export const SCHEDULED_REPORTS_QUERY_KEYS = {
  OVERVIEW: "scheduled-reports-overview",
  TOP_PERFORMERS: "scheduled-reports-top-performers",
  USERS_WITHOUT: "scheduled-reports-users-without",
  DIVISIONS: "scheduled-reports-divisions",
  SUPERVISORS: "scheduled-reports-supervisors",
  SAVED: "scheduled-reports-saved",
  SAVED_BY_ID: "scheduled-reports-saved-by-id",
  DISTRIBUTION_RECIPIENTS: "scheduled-reports-distribution-recipients",
} as const;

// Re-export generated DTOs; legacy names for backward compatibility
export type ScheduledReportOverview = ScheduledReportOverviewDto;
export type ScheduledReportTopPerformer = ScheduledReportTopPerformerDto;
export type ScheduledReportDivisionRow = ScheduledReportDivisionRowDto;
export type ScheduledReportSupervisorRow = ScheduledReportSupervisorRowDto;
export type ScheduledReportUserWithoutRow = ScheduledReportUserWithoutRowDto;
export type { DistributionRecipientDto as DistributionRecipient } from "../gen";
export type SavedReportMeta = SavedReportMetaDto;
export type SavedReportWithData = GetSavedReportByIdResponseDto;
export type { ScheduledReportDataDto as ScheduledReportData } from "../gen";

/** Params for overview/top-performers (date range only). */
export interface OverviewParams {
  dateStart: string;
  dateEnd: string;
}

/** Params for paginated endpoints (date range + page). */
export interface PaginatedParams extends OverviewParams {
  page?: number;
  pageSize?: number;
}

function toOverviewParams(p: OverviewParams): ScheduledReportsControllerGetOverviewParams {
  return { dateStart: p.dateStart, dateEnd: p.dateEnd };
}

function toPaginatedParams(
  p: PaginatedParams,
  defaultPage = 1,
  defaultPageSize = 10
): ScheduledReportsControllerGetUsersWithoutCompletionsParams {
  return {
    dateStart: p.dateStart,
    dateEnd: p.dateEnd,
    page: String(p.page ?? defaultPage),
    pageSize: String(p.pageSize ?? defaultPageSize),
  };
}

export function useGetScheduledReportsOverview(params: OverviewParams | null) {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetOverview } = getImproveAPI();
  return useQuery<ScheduledReportOverviewDto>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.OVERVIEW, params?.dateStart, params?.dateEnd],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetOverview(toOverviewParams(params!), axiosConfig);
      return res.data;
    },
    enabled: !!params?.dateStart && !!params?.dateEnd,
    staleTime: 2 * 60 * 1000,
  });
}

export function useGetScheduledReportsTopPerformers(params: OverviewParams | null) {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetTopPerformers } = getImproveAPI();
  return useQuery<ScheduledReportTopPerformerDto[]>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.TOP_PERFORMERS, params?.dateStart, params?.dateEnd],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetTopPerformers(toOverviewParams(params!) as ScheduledReportsControllerGetTopPerformersParams, axiosConfig);
      return res.data;
    },
    enabled: !!params?.dateStart && !!params?.dateEnd,
    staleTime: 2 * 60 * 1000,
  });
}

export function useGetScheduledReportsUsersWithout(params: PaginatedParams | null) {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetUsersWithoutCompletions } = getImproveAPI();
  return useQuery<GetUsersWithoutCompletionsResponseDto>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.USERS_WITHOUT, params],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetUsersWithoutCompletions(toPaginatedParams(params!), axiosConfig);
      return res.data;
    },
    enabled: !!params?.dateStart && !!params?.dateEnd,
    staleTime: 1 * 60 * 1000,
  });
}

export function useGetScheduledReportsDivisions(params: PaginatedParams | null) {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetDivisions } = getImproveAPI();
  return useQuery<GetDivisionsResponseDto>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.DIVISIONS, params],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetDivisions(toPaginatedParams(params!) as ScheduledReportsControllerGetDivisionsParams, axiosConfig);
      return res.data;
    },
    enabled: !!params?.dateStart && !!params?.dateEnd,
    staleTime: 1 * 60 * 1000,
  });
}

export function useGetScheduledReportsSupervisors(params: PaginatedParams | null) {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetSupervisors } = getImproveAPI();
  return useQuery<GetSupervisorsResponseDto>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SUPERVISORS, params],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetSupervisors(toPaginatedParams(params!) as ScheduledReportsControllerGetSupervisorsParams, axiosConfig);
      return res.data;
    },
    enabled: !!params?.dateStart && !!params?.dateEnd,
    staleTime: 1 * 60 * 1000,
  });
}

export function useGetDistributionRecipients() {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetDistributionRecipients } = getImproveAPI();
  return useQuery<GetDistributionRecipientsResponseDto>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.DISTRIBUTION_RECIPIENTS],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetDistributionRecipients(axiosConfig);
      return res.data;
    },
    staleTime: 1 * 60 * 1000,
  });
}

export function useAddDistributionRecipients() {
  const axiosConfig = useImproveAxios();
  const queryClient = useQueryClient();
  const { scheduledReportsControllerAddDistributionRecipients } = getImproveAPI();
  return useMutation<AddDistributionRecipientsResponseDto, Error, number[]>({
    mutationFn: async (userIds: number[]) => {
      const res = await scheduledReportsControllerAddDistributionRecipients({ userIds }, axiosConfig);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.DISTRIBUTION_RECIPIENTS] });
    },
  });
}

export function useRemoveDistributionRecipient() {
  const axiosConfig = useImproveAxios();
  const queryClient = useQueryClient();
  const { scheduledReportsControllerRemoveDistributionRecipient } = getImproveAPI();
  return useMutation<RemoveDistributionRecipientResponseDto, Error, number>({
    mutationFn: async (recipientId: number) => {
      const res = await scheduledReportsControllerRemoveDistributionRecipient(recipientId, axiosConfig);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.DISTRIBUTION_RECIPIENTS] });
    },
  });
}

export function useGetScheduledReportsSaved() {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetSavedReports } = getImproveAPI();
  return useQuery<SavedReportMetaDto[]>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetSavedReports(axiosConfig);
      return res.data;
    },
    staleTime: 1 * 60 * 1000,
  });
}

export function useGetScheduledReportById(id: number | null) {
  const axiosConfig = useImproveAxios();
  const { scheduledReportsControllerGetSavedReportById } = getImproveAPI();
  return useQuery<GetSavedReportByIdResponseDto>({
    queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED_BY_ID, id],
    queryFn: async () => {
      const res = await scheduledReportsControllerGetSavedReportById(id!, axiosConfig);
      return res.data;
    },
    enabled: id != null && id > 0,
    staleTime: 1 * 60 * 1000,
  });
}

export function useGenerateScheduledReport() {
  const axiosConfig = useImproveAxios();
  const queryClient = useQueryClient();
  const { scheduledReportsControllerGenerateReport } = getImproveAPI();
  return useMutation<SavedReportMetaDto, Error, GenerateReportBody>({
    mutationFn: async (body) => {
      const res = await scheduledReportsControllerGenerateReport(body, axiosConfig);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED] });
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED_BY_ID] });
    },
  });
}

export function useRegenerateScheduledReport() {
  const axiosConfig = useImproveAxios();
  const queryClient = useQueryClient();
  const { scheduledReportsControllerRegenerateReport } = getImproveAPI();
  return useMutation<SavedReportMetaDto, Error, GenerateReportBody>({
    mutationFn: async (body) => {
      const res = await scheduledReportsControllerRegenerateReport(body, axiosConfig);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED] });
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED_BY_ID] });
    },
  });
}

export function useDeleteScheduledReport() {
  const axiosConfig = useImproveAxios();
  const queryClient = useQueryClient();
  const { scheduledReportsControllerDeleteSavedReport } = getImproveAPI();
  return useMutation({
    mutationFn: async (id: number) => {
      await scheduledReportsControllerDeleteSavedReport(id, axiosConfig);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCHEDULED_REPORTS_QUERY_KEYS.SAVED] });
    },
  });
}

/** Returns the URL to download the PDF for a saved report (uses same auth as axios). */
export function getScheduledReportPdfDownloadUrl(reportId: number, baseUrl: string): string {
  return `${baseUrl}${BASE}/saved/${reportId}/pdf`;
}

/** Download report PDF using authenticated request; call from a hook to get axiosConfig. */
export async function downloadScheduledReportPdf(
  reportId: number,
  filename: string,
  axiosConfig: import("axios").AxiosRequestConfig
): Promise<void> {
  const { scheduledReportsControllerGetSavedReportPdf } = getImproveAPI();
  const response = await scheduledReportsControllerGetSavedReportPdf(reportId, { ...axiosConfig, responseType: "blob" });
  const blob = response.data as Blob;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename || "Scheduled-Report.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
